import { Menu } from "#components/primitive";
import React, { useCallback, useEffect, useState } from "react";

import { MenuIcon } from "../nav-bar/menu-icon";
import { MenuItem } from "#components/primitive/menu/v2/menu-item";
import { NavBarProps } from "./types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useStores } from "#stores/index";
import { MenuDivider } from "#components/primitive/menu/v2";
import { Link } from "react-router";
import { useChangeLayout } from "../use-change-layout";
import { isMain } from "#configuration/config";
import { useBreadcrumb } from "#structure/providers/breadcrumb";
import { extractUrlListFromNavItems } from "./helpers";
import { useMediaQuery } from "usehooks-ts";
import { MOBILE_MEDIA_QUERY } from "#components/primitive/menu/constants";

import s from "./nav-bar.module.scss";
import { SuportArea } from "#components/suport-area";
import { useGetCurrentEmployee } from "#resources/hooks/integrations/auth/query";
import { useAnalytics } from "#structure/providers/analytics/analytics-provider";
import { contextLevelFromUrl } from "#resources/helpers/url/url";

const _NavBar = ({
	isOpenSandwichMenu,
	items,
	runningEvents,
	onChangeIsOpenSandwichMenu,
	selectedMenu,
	onSelectMenu,
	showRunningEventsIdx,
}: NavBarProps) => {
	const { t } = useTranslation("nav", { keyPrefix: "navbar.default" });
	const { authStore, placeStore } = useStores();
	const [openSubMenuIndex, setOpenSubMenuIndex] = useState<number | null>(null);
	const { onChangeNavBarList, onChangeCurrentPage } = useBreadcrumb();
	const [isOpenHelpChat, setIsOpenHelpChat] = React.useState(false);

	const placeName = placeStore.place?.name;

	const { data: currentEmployee } = useGetCurrentEmployee({});
	const { sendEvent } = useAnalytics();

	const contextLevel = contextLevelFromUrl();

	const handleContactSupport = () => {
		sendEvent("SUPPORT", {
			actionType: "click",
			userName: currentEmployee?.name ?? "",
			userLogin: currentEmployee?.username ?? "",
			placeName: placeName ?? "",
			positionName: currentEmployee?.role?.name ?? "",
			dateUserAction: new Date().toISOString(),
			contextLevel,
		});
	};

	const hasPermissionToRequestWhatsAppSupport = currentEmployee?.permissions.some(
		permission => permission.slug === "requestWhatsAppSupport",
	);

	React.useEffect(() => {
		if (!items?.length) return;

		onChangeNavBarList(extractUrlListFromNavItems(items));
	}, [items]);

	const checkIfSubMenuShouldBeOpened = useCallback(() => {
		if (!selectedMenu) {
			return null;
		}

		const foundMenuItemWithSubItems = items.findIndex(item =>
			item.submenus?.some(
				subItem =>
					subItem.url === selectedMenu || subItem.alternativeUrls?.includes(selectedMenu),
			),
		);

		if (foundMenuItemWithSubItems > -1) {
			return foundMenuItemWithSubItems;
		}

		return null;
	}, [items, selectedMenu, isOpenSandwichMenu]);

	const { changeLayout } = useChangeLayout();

	useEffect(() => {
		if (!isOpenSandwichMenu) {
			const valueFound = checkIfSubMenuShouldBeOpened();
			setOpenSubMenuIndex(valueFound);
		}
	}, [isOpenSandwichMenu]);

	const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

	const menuItems = items.map((item, idx) => {
		return (
			<MenuItem
				{...item}
				key={`${item.url}-${item.label}`}
				icon={<MenuIcon icon={item.icon} className="menu-icon" width={24} height={24} />}
				onSelectMenu={menuUrl => {
					onSelectMenu?.(menuUrl);

					if (menuUrl) {
						onChangeCurrentPage(menuUrl);
					}

					if (isMobile) {
						onChangeIsOpenSandwichMenu(false);
					}
				}}
				selectedMenu={selectedMenu}
				showRunningEvents={idx === showRunningEventsIdx}
				isOpenSandwichMenu={isOpenSandwichMenu}
				runningEvents={runningEvents}
				subMenuItems={item.submenus}
				isSubMenuOpen={openSubMenuIndex === idx}
				onToggleSubMenu={forcedIndex => {
					const hasForcedIndex = typeof forcedIndex === "number";

					if (hasForcedIndex) {
						setOpenSubMenuIndex(forcedIndex);
						return;
					}

					setOpenSubMenuIndex(idx);
				}}
			/>
		);
	});

	return (
		<Menu
			title="Menu"
			onChangeIsOpenSandwichMenu={onChangeIsOpenSandwichMenu}
			isForceOpen={isOpenSandwichMenu}
			LogoContainer={({ children }) => <Link to="/">{children}</Link>}
			v2
		>
			{menuItems}
			<MenuDivider />
			{isMobile && hasPermissionToRequestWhatsAppSupport && (
				<>
					<SuportArea
						type="mobile"
						isOpen={isOpenHelpChat}
						placeName={placeName ?? ""}
						handleChangeIsOpen={isOpen => setIsOpenHelpChat(isOpen)}
					/>

					<MenuItem
						icon={<MenuIcon icon="help" width={24} height={24} />}
						label={"Central de ajuda"}
						onSelectMenu={() => {
							setIsOpenHelpChat(true);
							handleContactSupport();
						}}
						selectedMenu={null}
						isOpenSandwichMenu={isOpenSandwichMenu}
						className={!isMobile ? s.logoutButton : undefined}
					/>
				</>
			)}

			{!isMain && (
				<MenuItem
					icon={<MenuIcon icon="settings" width={24} height={24} />}
					label={t("changeLayout")}
					onSelectMenu={changeLayout}
					selectedMenu={null}
					isOpenSandwichMenu={isOpenSandwichMenu}
					className={!isMobile ? s.logoutButton : undefined}
				/>
			)}

			<MenuItem
				icon={<MenuIcon icon="logout" width={24} height={24} />}
				label={t("logout")}
				onSelectMenu={() => authStore.logout()}
				selectedMenu={selectedMenu}
				isOpenSandwichMenu={isOpenSandwichMenu}
				className={!isMobile ? s.logoutButton : undefined}
			/>
		</Menu>
	);
};

export const NavBar = observer(_NavBar);
